/**
 * Season Data Export Service
 * Generates ZIP file with JSON, CSV, and photos for field season data
 */
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Convert array of objects to CSV string
const arrayToCSV = (data, columns = null) => {
  if (!data || data.length === 0) return '';
  
  const headers = columns || Object.keys(data[0]);
  const csvRows = [];
  
  // Header row
  csvRows.push(headers.join(','));
  
  // Data rows
  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header];
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return JSON.stringify(val).replace(/"/g, '""');
      const stringVal = String(val);
      // Escape quotes and wrap in quotes if contains comma or newline
      if (stringVal.includes(',') || stringVal.includes('\n') || stringVal.includes('"')) {
        return `"${stringVal.replace(/"/g, '""')}"`;
      }
      return stringVal;
    });
    csvRows.push(values.join(','));
  }
  
  return csvRows.join('\n');
};

// Generate README.txt content
const generateReadme = (field, exportDate) => {
  return `SFarm Monitor - Season Data Export
=====================================

Field: ${field.name}
Crop: ${field.crop || 'N/A'}
Export Date: ${exportDate}
Sowing Date: ${field.sowing_date || 'N/A'}
Harvest Date: ${field.harvest_date || 'N/A'}

This export contains all season data for the field including:
- season_summary.csv: Overview of the growing season
- activities.csv: All logged field activities with costs
- scouting_points.csv: Scouting observations and checklist results
- tasks.csv: Tasks created from alerts and manually
- alerts.csv: Disease/pest/weather alerts generated
- weather_daily.csv: Daily weather history
- field.json: Complete field data in JSON format

Photos folder contains all scouting photos if "Export with photos" was selected.

Generated by SFarm Monitor
`;
};

// Generate season summary CSV
const generateSeasonSummary = (field, activities, scoutingPoints, alerts) => {
  const totalCost = activities.reduce((sum, a) => sum + (a.totalCost || 0), 0);
  const areaHa = field.areaHa || 0;
  
  const summary = [{
    fieldName: field.name,
    crop: field.crop || '',
    previousCrop: field.previous_crop || '',
    sowingDate: field.sowing_date || '',
    harvestDate: field.harvest_date || '',
    areaHa: areaHa.toFixed(2),
    totalGDD: field.gdd?.toFixed(1) || '0',
    totalRainMm: field.rain_accumulation?.toFixed(1) || '0',
    totalActivities: activities.length,
    totalScoutingPoints: scoutingPoints.length,
    totalAlerts: alerts.length,
    totalSeasonCost: totalCost.toFixed(2),
    costPerHa: areaHa > 0 ? (totalCost / areaHa).toFixed(2) : '0'
  }];
  
  return arrayToCSV(summary);
};

// Generate activities CSV
const generateActivitiesCSV = (activities) => {
  const columns = [
    'date', 'activity_type', 'productName', 'quantityPerHa', 'quantityUnit',
    'costPerHa', 'totalCost', 'notes', 'created_at'
  ];
  
  const data = activities.map(a => ({
    date: a.date || '',
    activity_type: a.activity_type || '',
    productName: a.productName || '',
    quantityPerHa: a.quantityPerHa || '',
    quantityUnit: a.quantityUnit || '',
    costPerHa: a.costPerHa || '',
    totalCost: a.totalCost || '',
    notes: a.notes || '',
    created_at: a.created_at || ''
  }));
  
  return arrayToCSV(data, columns);
};

// Generate scouting points CSV
const generateScoutingCSV = (scoutingPoints) => {
  const columns = [
    'created_at', 'lat', 'lng', 'status',
    'emergence', 'weeds', 'pests', 'disease', 'nutrientDeficiency', 'waterStress',
    'notes', 'photoCount'
  ];
  
  const data = scoutingPoints.map(sp => ({
    created_at: sp.createdAt || '',
    lat: sp.lat || '',
    lng: sp.lng || '',
    status: sp.status || '',
    emergence: sp.checklist?.emergence ? 'Yes' : 'No',
    weeds: sp.checklist?.weeds ? 'Yes' : 'No',
    pests: sp.checklist?.pests ? 'Yes' : 'No',
    disease: sp.checklist?.disease ? 'Yes' : 'No',
    nutrientDeficiency: sp.checklist?.nutrientDeficiency ? 'Yes' : 'No',
    waterStress: sp.checklist?.waterStress ? 'Yes' : 'No',
    notes: sp.notes || '',
    photoCount: sp.photos?.length || 0
  }));
  
  return arrayToCSV(data, columns);
};

// Generate tasks CSV
const generateTasksCSV = (tasks) => {
  const columns = [
    'title', 'details', 'source', 'dueDate', 'status',
    'createdAt', 'completedAt'
  ];
  
  const data = tasks.map(t => ({
    title: t.title || '',
    details: t.details || '',
    source: t.source || '',
    dueDate: t.dueDate || '',
    status: t.status || '',
    createdAt: t.createdAt || '',
    completedAt: t.completedAt || ''
  }));
  
  return arrayToCSV(data, columns);
};

// Generate alerts CSV
const generateAlertsCSV = (alerts) => {
  const columns = [
    'timestamp', 'type', 'severity', 'title', 'summary', 'why', 'actions'
  ];
  
  const data = alerts.map(a => ({
    timestamp: a.timestamp || '',
    type: a.type || '',
    severity: a.severity || '',
    title: a.title || '',
    summary: a.summary || '',
    why: Array.isArray(a.why) ? a.why.join('; ') : (a.why || ''),
    actions: Array.isArray(a.actions) ? a.actions.join('; ') : (a.actions || '')
  }));
  
  return arrayToCSV(data, columns);
};

// Generate weather history CSV
const generateWeatherCSV = (weatherHistory) => {
  const columns = [
    'date', 'temp_max', 'temp_min', 'temp_avg', 'humidity_avg',
    'precip_mm', 'gdd_contribution'
  ];
  
  const data = weatherHistory.map(w => ({
    date: w.date || '',
    temp_max: w.temp_max?.toFixed(1) || '',
    temp_min: w.temp_min?.toFixed(1) || '',
    temp_avg: w.temp_avg?.toFixed(1) || '',
    humidity_avg: w.humidity_avg?.toFixed(0) || '',
    precip_mm: w.precip_mm?.toFixed(1) || '',
    gdd_contribution: w.gdd_contribution?.toFixed(1) || ''
  }));
  
  return arrayToCSV(data, columns);
};

/**
 * Export all season data for a field
 * @param {Object} params - Export parameters
 * @param {Object} params.field - Field data
 * @param {Array} params.activities - Field activities
 * @param {Array} params.scoutingPoints - Scouting points
 * @param {Array} params.tasks - Tasks
 * @param {Array} params.alerts - Alerts
 * @param {Array} params.weatherHistory - Weather history
 * @param {Array} params.summaries - AI summaries
 * @param {Array} params.ndviReadings - NDVI readings
 * @param {Array} params.cases - Cases/agronomist chats
 * @param {Array} params.caseMessages - Case messages
 * @param {boolean} params.includePhotos - Whether to include photos
 * @param {Function} params.onProgress - Progress callback (0-100)
 * @returns {Promise<void>}
 */
export const exportSeasonData = async ({
  field,
  activities = [],
  scoutingPoints = [],
  tasks = [],
  alerts = [],
  weatherHistory = [],
  summaries = [],
  ndviReadings = [],
  cases = [],
  caseMessages = [],
  includePhotos = false,
  onProgress = () => {}
}) => {
  const zip = new JSZip();
  const exportDate = new Date().toISOString().split('T')[0];
  const fileName = `${field.name.replace(/[^a-z0-9]/gi, '_')}_season_${exportDate}.zip`;
  
  onProgress(5);
  
  // README
  zip.file('README.txt', generateReadme(field, exportDate));
  onProgress(10);
  
  // Season summary CSV
  zip.file('season_summary.csv', generateSeasonSummary(field, activities, scoutingPoints, alerts));
  onProgress(15);
  
  // Activities CSV
  zip.file('activities.csv', generateActivitiesCSV(activities));
  onProgress(20);
  
  // Scouting points CSV
  zip.file('scouting_points.csv', generateScoutingCSV(scoutingPoints));
  onProgress(25);
  
  // Tasks CSV
  zip.file('tasks.csv', generateTasksCSV(tasks));
  onProgress(30);
  
  // Alerts CSV
  zip.file('alerts.csv', generateAlertsCSV(alerts));
  onProgress(35);
  
  // Weather history CSV
  if (weatherHistory.length > 0) {
    zip.file('weather_daily.csv', generateWeatherCSV(weatherHistory));
  }
  onProgress(40);
  
  // Complete field JSON
  const fieldJson = {
    field,
    activities,
    scoutingPoints: scoutingPoints.map(sp => ({ ...sp, photos: sp.photos?.map(p => p.storagePath) })),
    tasks,
    alerts,
    weatherHistory,
    summaries,
    ndviReadings,
    cases,
    caseMessages,
    exportedAt: new Date().toISOString()
  };
  zip.file('export.json', JSON.stringify(fieldJson, null, 2));
  onProgress(50);
  
  // Photos (if requested)
  if (includePhotos) {
    const photosFolder = zip.folder('photos');
    let photoIndex = 0;
    const totalPhotos = scoutingPoints.reduce((sum, sp) => sum + (sp.photos?.length || 0), 0);
    
    for (const sp of scoutingPoints) {
      if (sp.photos && sp.photos.length > 0) {
        for (const photo of sp.photos) {
          try {
            // Fetch photo from storage path
            const response = await fetch(photo.url || photo.storagePath);
            const blob = await response.blob();
            const ext = photo.storagePath?.split('.').pop() || 'jpg';
            photosFolder.file(`scout_${sp.id}_${photoIndex}.${ext}`, blob);
            photoIndex++;
            
            // Update progress for photos (50-90%)
            const photoProgress = 50 + (40 * photoIndex / Math.max(totalPhotos, 1));
            onProgress(Math.min(photoProgress, 90));
          } catch (e) {
            console.error('Failed to fetch photo:', photo.storagePath, e);
          }
        }
      }
    }
  }
  
  onProgress(95);
  
  // Generate and download ZIP
  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, fileName);
  
  onProgress(100);
  
  return { success: true, fileName };
};

export default { exportSeasonData };
